import { useEffect, useRef } from "react";
import { QueryFunctionContext, useQuery } from "react-query";

import axios from "axios";

import { FORECASTING_SERVICE_ADDRESS } from "../../../constants/app.constants";
import { HierarchyFilters } from "../contexts/ForecastingProvider";
import { useForecastingContext } from "../contexts/useForecastingContext";
import {
  ForecastWell,
  ForecastWellStatus,
  WellHierarchy,
  WellHierarchyCollection
} from "../models/forecastWell";
import { ForecastHierarchy } from "../models/hierarchy";

const API_URL = `${FORECASTING_SERVICE_ADDRESS}/api/v1/hierarchy`;

export const FetchWellHierarchyQueryKey = "fetchWellHierarchy";

const fetchAllHierarchies = async (
  keys: QueryFunctionContext
): Promise<WellHierarchyCollection> => {
  const hierarchy =
    keys.queryKey.length > 1 ? (keys.queryKey[1] as ForecastHierarchy) : { id: "" };
  if (!hierarchy?.id) return { hierarchy: [], leafs: [] } as WellHierarchyCollection;
  const filterId = (keys.queryKey.length > 2 ? keys.queryKey[2] : { id: "" }) as {
    id: string;
  };
  const response = await axios.get<WellHierarchy[]>(
    `${API_URL}/${hierarchy?.id}/well/${filterId.id}`
  );
  const data = response.data;
  const leafs = populateKeys("root", data);
  return {
    hierarchy: data,
    leafs
  } as WellHierarchyCollection;
};

const populateKeys = (parentTitle: string, data: WellHierarchy[]): ForecastWell[] => {
  let leafs = [];
  for (const item of data) {
    item.title = item.hierarchyName;
    item.selectable = false;
    item.key = parentTitle + "~>" + item.title;
    leafs = leafs.concat(populateKeys(item.key, item.children));
    if (item.leafs.length > 0) {
      for (const leaf of item.leafs) {
        leaf.title = leaf.entityName;
        leaf.key = leaf.uwi;
        leaf.selectable = true;
        leaf.parentKey = item.key;
      }
      item.children = item.leafs as unknown as WellHierarchy[];
      leafs = leafs.concat(...item.leafs);
    }
  }
  return leafs;
};

function doesForecastWellMatchFilters(
  leaf: ForecastWell,
  filters: HierarchyFilters
): boolean {
  const showErrors = filters.showErrorsOnly;
  const showWarnings = filters.showWarningsOnly;

  if (showErrors && showWarnings) {
    if (!leaf.hasErrors && !leaf.hasWarnings) return false;
  } else if (showErrors) {
    if (!leaf.hasErrors) return false;
  } else if (showWarnings) {
    if (!leaf.hasWarnings) return false;
  }

  const statusFilter = filters.statusFilter;
  if (statusFilter) {
    if (statusFilter === ForecastWellStatus.PDPUpdated) {
      if (!leaf.approved) return false;
    } else if (statusFilter === ForecastWellStatus.NotPDPUpdated) {
      if (leaf.approved) return false;
    } else if (statusFilter === ForecastWellStatus.MissingForecast) {
      if (!leaf.missingForecast) return false;
    } else if (
      Object.values(ForecastWellStatus).includes(statusFilter as ForecastWellStatus)
    ) {
      if (leaf.status !== (statusFilter as ForecastWellStatus)) return false;
    }
  }
  return true;
}
/**
 * Filters the well hierarchy tree and flat leaf list according to the provided filters.
 *
 * - The tree (hierarchy) is filtered recursively so that only branches (groups/folders) and wells that match the filters are shown in the UI tree view.
 * This preserves the hierarchical structure, but prunes out any empty branches and non-matching wells.
 *
 * - The flat leafs array is filtered separately to provide a flat list of all wells that match the filters, regardless of their position in the hierarchy.
 * This is useful for bulk actions, selection, and other logic that operates on wells directly.
 *
 * Both filters use the same well-matching logic to ensure consistency, but the data structures serve different UI and logic needs.
 */
const filterHierarchy = (
  hierarchyCollection: WellHierarchyCollection,
  filters: HierarchyFilters
): WellHierarchyCollection => {
  // If no filters are active, return original data
  if (!filters.showErrorsOnly && !filters.showWarningsOnly && !filters.statusFilter) {
    return hierarchyCollection;
  }

  const filterNode = (node: WellHierarchy): WellHierarchy | null => {
    const children = Array.isArray(node.children) ? node.children : [];
    const leafs = Array.isArray(node.leafs) ? node.leafs : [];

    // Only filter out empty children AFTER filtering them recursively
    const filteredChildren = children.map(filterNode).filter(Boolean) as WellHierarchy[];

    // Filter leafs (wells) based on active filters
    const filteredLeafs = leafs.filter((leaf) =>
      doesForecastWellMatchFilters(leaf, filters)
    );

    // Only keep node if it has filtered children or filtered leafs
    if (
      (filteredChildren && filteredChildren.length > 0) ||
      (filteredLeafs && filteredLeafs.length > 0)
    ) {
      return {
        ...node,
        children:
          filteredChildren.length > 0
            ? filteredChildren
            : (filteredLeafs as unknown as WellHierarchy[]),
        leafs: filteredLeafs
      };
    }

    // If both are empty, remove this node
    return null;
  };

  const filteredHierarchy = hierarchyCollection.hierarchy
    .map(filterNode)
    .filter(Boolean) as WellHierarchy[];

  // Also filter the flat leafs array
  const filteredLeafs = hierarchyCollection.leafs.filter((leaf) =>
    doesForecastWellMatchFilters(leaf, filters)
  );

  return {
    hierarchy: filteredHierarchy,
    leafs: filteredLeafs
  };
};

const DEFAULT_FILTERS: HierarchyFilters = {
  showErrorsOnly: false,
  showWarningsOnly: false,
  statusFilter: null
};

const useFetchWellHierarchy = (
  hierarchy: ForecastHierarchy,
  filterId: { id: string; lastUpdated: number; isVisible?: boolean }
) => {
  const { state, dispatch } = useForecastingContext();
  const previousHierarchyIdRef = useRef(state.selectedHierarchy?.id);

  const result = useQuery<WellHierarchyCollection>(
    [
      FetchWellHierarchyQueryKey,
      hierarchy,
      { id: filterId.id, lastUpdated: filterId.lastUpdated }
    ],
    fetchAllHierarchies,
    {
      refetchOnWindowFocus: false,
      //disable this if show nearby wells is on
      //because we don't want to refetch the hierarchy
      //since the map would have changed
      enabled:
        (filterId.isVisible ?? true) &&
        (!state.zoomToWell.zoom || previousHierarchyIdRef.current !== hierarchy?.id),
      onSuccess: () => {
        previousHierarchyIdRef.current = hierarchy?.id;
      }
    }
  );

  useEffect(() => {
    if (result.data) {
      const filters = state.filters ?? DEFAULT_FILTERS;
      dispatch({
        type: "SET_ORIGINAL_WELL_HIERARCHY",
        payload: result.data
      });

      // Apply filters and set filtered data
      const filteredData = filterHierarchy(result.data, filters);
      dispatch({
        type: "SET_WELL_HIERARCHY",
        payload: filteredData
      });
    }
  }, [result.data, dispatch, state.filters]);

  // Watch for filter changes and re-apply to original data
  useEffect(() => {
    if (state.originalWellHierarchy) {
      const filters = state.filters ?? DEFAULT_FILTERS;
      const filteredData = filterHierarchy(state.originalWellHierarchy, filters);
      dispatch({
        type: "SET_WELL_HIERARCHY",
        payload: filteredData
      });
    }
  }, [state.filters, state.originalWellHierarchy, dispatch]);

  useEffect(() => {
    dispatch({
      type: "SET_WELL_HIERARCHY_LOADING",
      payload: result.isLoading
    });
  }, [result.isLoading, dispatch]);

  return result;
};

export default useFetchWellHierarchy;
